export const DEFAULT_RECIPE_FUNCTION_URL = 'https://us-central1-fusco-fit-connect-4b731.cloudfunctions.net/generateRecipe';
export const LOCAL_RECIPE_FUNCTION_URL = 'http://127.0.0.1:8791/generateRecipe';
export const RECIPE_REQUEST_TIMEOUT_MS = 45000;

export function isLoopbackHost(hostname = '') {
  return ['localhost', '127.0.0.1', '::1', '[::1]'].includes(String(hostname).toLowerCase());
}

export function resolveRecipeFunctionUrl({ development = false, hostname = '', override = '' } = {}) {
  if (typeof override === 'string' && override.trim()) return override.trim();
  return development && isLoopbackHost(hostname)
    ? LOCAL_RECIPE_FUNCTION_URL
    : DEFAULT_RECIPE_FUNCTION_URL;
}

export function isLocalRecipeEndpoint(endpoint) {
  try {
    return isLoopbackHost(new URL(endpoint).hostname);
  } catch (error) {
    return false;
  }
}

export function createRecipePayload(form) {
  return {
    mealType: form.mealType,
    flavorType: form.flavorType,
    carbsIngredient: form.carbsGrams === 'Nessun carbo' ? '' : form.carbsIngredient.trim(),
    carbsGrams: form.carbsGrams,
    proteinIngredient: form.proteinIngredient.trim(),
    proteinGrams: form.proteinGrams,
    fatIngredient: form.fatIngredient.trim(),
    fatGrams: form.fatGrams,
    recipeStyle: form.recipeStyle,
    ingredientsToExclude: form.ingredientsToExclude.trim(),
  };
}

export function formatRecipeText(recipe) {
  if (typeof recipe !== 'string' || !recipe) return '';

  const escaped = recipe.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[character]));

  return escaped.replace(/\r\n?/g, '\n').split('\n').map((line) => {
    const formatted = line.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    if (/^\s*#{1,3}\s+/.test(formatted)) {
      return formatted.replace(/^\s*#{1,3}\s+(.+)$/, '<strong>$1</strong>');
    }
    return formatted.replace(
      /^(\s*)(Ingredienti|Istruzioni|Preparazione|Processo|Parte 1|Parte 2|Parte II|Parte I|Prima Parte|Seconda Parte)\b/,
      '$1<strong>$2</strong>',
    );
  }).join('<br>');
}

function recipeError(code, message) {
  const error = new Error(message);
  error.name = 'RecipeClientError';
  error.code = code;
  return error;
}

export async function requestRecipe({
  endpoint,
  payload,
  user,
  signal,
  timeoutMs = RECIPE_REQUEST_TIMEOUT_MS,
  fetchImpl = fetch,
}) {
  if (!user || typeof user.getIdToken !== 'function') {
    throw recipeError('AUTH_REQUIRED', 'Accedi al tuo account per creare una ricetta.');
  }
  if (signal && signal.aborted) {
    throw recipeError('REQUEST_CANCELLED', 'Richiesta annullata.');
  }

  const controller = new AbortController();
  let timedOut = false;
  const abortRequest = () => controller.abort();
  const interruptionError = () => timedOut
    ? recipeError('REQUEST_TIMEOUT', 'La creazione sta impiegando troppo tempo. Riprova tra poco.')
    : recipeError('REQUEST_CANCELLED', 'Richiesta annullata.');
  let rejectInterrupted;
  const interrupted = new Promise((resolve, reject) => { rejectInterrupted = reject; });
  const handleAbort = () => rejectInterrupted(interruptionError());
  controller.signal.addEventListener('abort', handleAbort, { once: true });
  if (signal) signal.addEventListener('abort', abortRequest, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const performRequest = async () => {
    let token;
    try {
      token = await user.getIdToken();
    } catch (error) {
      throw recipeError('AUTH_REQUIRED', 'La sessione è scaduta. Accedi di nuovo per creare una ricetta.');
    }
    if (controller.signal.aborted) throw interruptionError();
    if (typeof token !== 'string' || !token.trim()) {
      throw recipeError('AUTH_REQUIRED', 'La sessione è scaduta. Accedi di nuovo per creare una ricetta.');
    }

    const response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (error) {
      throw recipeError('INVALID_RESPONSE', 'Il servizio non ha restituito una risposta valida. Riprova.');
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw recipeError('INVALID_RESPONSE', 'Il servizio non ha restituito una risposta valida. Riprova.');
    }
    if (!response.ok || data.error) {
      throw recipeError(
        typeof data.code === 'string' ? data.code : 'GENERATION_FAILED',
        typeof data.error === 'string' && data.error.trim()
          ? data.error.trim()
          : 'Impossibile creare la ricetta. Riprova tra poco.',
      );
    }
    if (typeof data.recipe !== 'string' || !data.recipe.trim()) {
      throw recipeError('EMPTY_RECIPE', 'La ricetta ricevuta è vuota. Riprova tra poco.');
    }
    return data.recipe.trim();
  };

  try {
    return await Promise.race([performRequest(), interrupted]);
  } catch (error) {
    if (controller.signal.aborted) throw interruptionError();
    if (error && error.name === 'RecipeClientError') throw error;
    throw isLocalRecipeEndpoint(endpoint)
      ? recipeError('LOCAL_API_UNAVAILABLE', 'Il servizio ricette locale non è raggiungibile. Verifica che sia avviato su 127.0.0.1:8791.')
      : recipeError('NETWORK_ERROR', 'Non riesco a contattare il servizio ricette. Controlla la connessione e riprova.');
  } finally {
    clearTimeout(timer);
    controller.signal.removeEventListener('abort', handleAbort);
    if (signal) signal.removeEventListener('abort', abortRequest);
  }
}
